import React, { useState, useEffect } from 'react';
import axios from 'axios';

//const API = process.env.REACT_APP_API_URL || 'https://sohaibenterprises.cloud/api';
const API = 'http://localhost:5000/api';

// ── Reusable stat card component ──
const StatCard = ({ icon, label, value, sub, color }) => (
  <div style={{
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
    borderLeft: `4px solid ${color}`,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  }}>
    <div style={{ fontSize: '32px' }}>{icon}</div>
    <div style={{ fontSize: '14px', color: '#7f8c8d', fontWeight: '500' }}>{label}</div>
    <div style={{ fontSize: '28px', fontWeight: '700', color: '#2c3e50' }}>{value}</div>
    {sub && <div style={{ fontSize: '13px', color: '#95a5a6' }}>{sub}</div>}
  </div>
);

const DashboardTab = () => {
  const [inventory, setInventory]       = useState([]);
  const [rawMaterials, setRawMaterials] = useState([]);
  const [sales, setSales]               = useState([]);
  const [loading, setLoading]           = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    // Fetch all data at once using Promise.allSettled
    // allSettled means: even if one request fails, others still work
    Promise.allSettled([
      axios.get(`${API}/tape/inventory`, { headers }),
      axios.get(`${API}/tape/raw-materials`, { headers }),
      axios.get(`${API}/tape/sales`, { headers }),
    ]).then(([inv, raw, sal]) => {
      if (inv.status === 'fulfilled') setInventory(inv.value.data.data || []);
      if (raw.status === 'fulfilled') setRawMaterials(raw.value.data.data || []);
      if (sal.status === 'fulfilled') setSales(sal.value.data.data || []);
      setLoading(false);
    });
  }, []);

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '60px', fontSize: '18px', color: '#7f8c8d' }}>
      Loading dashboard...
    </div>
  );

  // ── Calculate summary numbers ──
  const totalFinishedRolls  = inventory.reduce((sum, i) => sum + (i.current_stock || 0), 0);
  const totalRawMeters      = rawMaterials.reduce((sum, r) => sum + parseFloat(r.remaining_meters || 0), 0);
  const totalSalesAmount    = sales.reduce((sum, s) => sum + parseFloat(s.total_amount || 0), 0);
  const lowStockItems       = inventory.filter(i => i.current_stock <= i.low_stock_alert);

  const fmt = (n) => `Rs ${Number(n || 0).toLocaleString()}`;

  return (
    <div>
      <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#2c3e50', marginBottom: '24px' }}>
        Adhesive Tapes Overview
      </h2>

      {/* ── STAT CARDS ── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '20px',
        marginBottom: '32px'
      }}>
        <StatCard icon="📦" label="Total Finished Rolls"  value={totalFinishedRolls}           sub="In stock"          color="#f39c12" />
        <StatCard icon="🎞️" label="Raw Material Remaining" value={`${totalRawMeters.toFixed(0)}m`} sub="Meters left"   color="#3498db" />
        <StatCard icon="💰" label="Total Sales"            value={fmt(totalSalesAmount)}         sub="All time"         color="#27ae60" />
        <StatCard icon="⚠️" label="Low Stock Alerts"       value={lowStockItems.length}          sub="Products low"     color="#e74c3c" />
      </div>

      {/* ── INVENTORY STATUS TABLE ── */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
        marginBottom: '24px'
      }}>
        <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#2c3e50', marginBottom: '20px' }}>
          Current Inventory Status
        </h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8f9fa' }}>
                {['Product', 'Size', 'Type', 'Stock', 'Status'].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px', color: '#7f8c8d', fontWeight: '600' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {inventory.map((item, index) => {
                const isLow = item.current_stock <= item.low_stock_alert;
                return (
                  <tr key={item.id} style={{ borderBottom: '1px solid #f0f0f0', background: index % 2 === 0 ? 'white' : '#fafafa' }}>
                    <td style={{ padding: '12px 16px', fontWeight: '600', color: '#2c3e50' }}>{item.product_name}</td>
                    <td style={{ padding: '12px 16px', color: '#555' }}>{item.size_inches}"</td>
                    <td style={{ padding: '12px 16px', color: '#555', textTransform: 'capitalize' }}>{item.type}</td>
                    <td style={{ padding: '12px 16px', fontWeight: '700', color: isLow ? '#e74c3c' : '#27ae60' }}>
                      {item.current_stock} rolls
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: '600',
                        background: isLow ? '#fdecea' : '#eafaf1',
                        color: isLow ? '#e74c3c' : '#27ae60'
                      }}>
                        {isLow ? '⚠️ Low Stock' : '✅ OK'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── RECENT SALES ── */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.08)'
      }}>
        <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#2c3e50', marginBottom: '20px' }}>
          Recent Sales
        </h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8f9fa' }}>
                {['Customer', 'Product', 'Qty', 'Total', 'Date'].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px', color: '#7f8c8d', fontWeight: '600' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sales.slice(0, 5).map((sale, index) => (
                <tr key={sale.id} style={{ borderBottom: '1px solid #f0f0f0', background: index % 2 === 0 ? 'white' : '#fafafa' }}>
                  <td style={{ padding: '12px 16px', fontWeight: '600', color: '#2c3e50' }}>{sale.customer_name || 'Walk-in'}</td>
                  <td style={{ padding: '12px 16px', color: '#555' }}>{sale.product_name}</td>
                  <td style={{ padding: '12px 16px', color: '#555' }}>{sale.quantity_sold} rolls</td>
                  <td style={{ padding: '12px 16px', fontWeight: '700', color: '#27ae60' }}>{fmt(sale.total_amount)}</td>
                  <td style={{ padding: '12px 16px', color: '#7f8c8d' }}>{new Date(sale.sale_date).toLocaleDateString()}</td>
                </tr>
              ))}
              {sales.length === 0 && (
                <tr>
                  <td colSpan="5" style={{ padding: '40px', textAlign: 'center', color: '#7f8c8d' }}>No sales yet</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DashboardTab;